import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, ArrowRight, Car, CheckCircle } from "lucide-react";
import { CameraCapture } from "../../components/ui/camera-capture";
import { AiAvatar, KIOSK_MESSAGES } from "../../components/ui/ai-avatar";

const IDLE_TIMEOUT = 60;

export default function KioskBadgeCapture() {
  const navigate = useNavigate();
  const location = useLocation();
  const registrationData = location.state?.registrationData;

  const [currentSide, setCurrentSide] = useState("front"); // "front" or "back"
  const [frontImage, setFrontImage] = useState(null);
  const [backImage, setBackImage] = useState(null);
  const [frontPreview, setFrontPreview] = useState(null);
  const [backPreview, setBackPreview] = useState(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const idleTimerRef = useRef(null);

  // Idle timeout - return to attract after 60s inactivity (paused during active capture)
  useEffect(() => {
    const resetIdleTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      // Only set timeout if not actively capturing
      if (!isCapturing) {
        idleTimerRef.current = setTimeout(() => navigate('/kiosk/attract'), IDLE_TIMEOUT * 1000);
      }
    };
    const events = ['mousedown', 'touchstart', 'keydown'];
    events.forEach(e => document.addEventListener(e, resetIdleTimer, { passive: true }));
    resetIdleTimer();
    return () => {
      events.forEach(e => document.removeEventListener(e, resetIdleTimer));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [navigate, isCapturing]);

  useEffect(() => {
    if (!registrationData) {
      navigate("/kiosk/category");
    }
  }, [registrationData, navigate]);

  if (!registrationData) {
    return null;
  }

  const handleFrontCapture = (base64, previewUrl) => {
    setIsCapturing(false);
    setFrontImage(base64);
    setFrontPreview(previewUrl);
    // Auto-advance to back capture after front is done
    if (base64) {
      setTimeout(() => setCurrentSide("back"), 500);
    }
  };

  const handleBackCapture = (base64, previewUrl) => {
    setIsCapturing(false);
    setBackImage(base64);
    setBackPreview(previewUrl);
  };

  const handleCaptureStart = () => {
    setIsCapturing(true);
  };

  const handleContinue = () => {
    if (frontImage && backImage) {
      navigate("/kiosk/processing", {
        state: {
          registrationData,
          badgeImages: {
            front: frontImage,
            back: backImage
          }
        }
      });
    }
  };

  // Dynamic avatar message based on capture state
  const getAvatarMessage = () => {
    if (!frontImage) {
      return "Please capture the front of your Blue Badge. Hold the badge steady in front of the camera so all text is clearly visible.";
    }
    if (!backImage) {
      return "Great! Now please capture the back of your Blue Badge. Make sure the expiry date is clearly visible.";
    }
    return "Both sides captured successfully! Tap Process Badge to continue with verification.";
  };

  return (
    <div className="kiosk-container flex flex-col min-h-screen p-6 md:p-12" data-testid="kiosk-badge-capture">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-blue-600 to-green-500 flex items-center justify-center">
            <Car className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
            ParkWise
          </span>
        </div>
        
        {/* Progress */}
        <div className="flex items-center gap-2">
          <div className="progress-step completed">1</div>
          <div className="progress-line completed w-8"></div>
          <div className="progress-step completed">2</div>
          <div className="progress-line completed w-8"></div>
          <div className="progress-step active">3</div>
        </div>
      </div>

      {/* AI Avatar */}
      <div className="flex justify-center mb-6">
        <AiAvatar 
          message={getAvatarMessage()} 
          size="medium"
          autoSpeak={true}
        />
      </div>

      {/* Title */}
      <div className="text-center mb-6">
        <h1 
          className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          Capture Blue Badge
        </h1>
        <p className="text-lg text-slate-600">
          Use the camera to capture both sides of your Blue Badge
        </p>
      </div>

      {/* Side selector tabs */}
      <div className="flex justify-center gap-4 mb-6">
        <button
          onClick={() => setCurrentSide("front")}
          className={`px-6 py-3 rounded-xl font-semibold transition-all flex items-center gap-2 ${
            currentSide === "front"
              ? "bg-blue-600 text-white shadow-lg"
              : frontImage
              ? "bg-green-100 text-green-700 border-2 border-green-300"
              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
          }`}
          data-testid="tab-front"
        >
          {frontImage && <CheckCircle className="w-5 h-5" />}
          Front of Badge
        </button>
        <button
          onClick={() => setCurrentSide("back")}
          className={`px-6 py-3 rounded-xl font-semibold transition-all flex items-center gap-2 ${
            currentSide === "back"
              ? "bg-blue-600 text-white shadow-lg"
              : backImage
              ? "bg-green-100 text-green-700 border-2 border-green-300"
              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
          }`}
          data-testid="tab-back"
        >
          {backImage && <CheckCircle className="w-5 h-5" />}
          Back of Badge
        </button>
      </div>

      {/* Camera capture area */}
      <div className="flex-1 max-w-2xl mx-auto w-full">
        {currentSide === "front" ? (
          <CameraCapture
            key="front-camera"
            onCapture={handleFrontCapture}
            label="Capture Front"
            capturedImage={frontPreview}
          />
        ) : (
          <CameraCapture
            key="back-camera"
            onCapture={handleBackCapture}
            label="Capture Back"
            capturedImage={backPreview}
          />
        )}
      </div>

      {/* Capture status indicators */}
      <div className="max-w-2xl mx-auto w-full mt-4">
        <div className="flex justify-center gap-8">
          <div className={`flex items-center gap-2 ${frontImage ? "text-green-600" : "text-slate-400"}`}>
            <div className={`w-3 h-3 rounded-full ${frontImage ? "bg-green-500" : "bg-slate-300"}`} />
            <span className="font-medium">Front {frontImage ? "✓" : "required"}</span>
          </div>
          <div className={`flex items-center gap-2 ${backImage ? "text-green-600" : "text-slate-400"}`}>
            <div className={`w-3 h-3 rounded-full ${backImage ? "bg-green-500" : "bg-slate-300"}`} />
            <span className="font-medium">Back {backImage ? "✓" : "required"}</span>
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="max-w-2xl mx-auto w-full mt-4 p-4 bg-blue-50 rounded-xl border border-blue-200">
        <p className="text-blue-800 text-center text-sm">
          <strong>Tip:</strong> Ensure good lighting and hold the badge flat. 
          The camera will capture your badge image for automatic verification.
        </p>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between mt-6 pt-6 border-t-2 border-slate-200">
        <button
          onClick={() => navigate("/kiosk/form", { 
            state: { 
              category: { 
                id: "disabled_blue_badge", 
                name: "Disabled Blue Badge", 
                requiresOCR: true 
              } 
            } 
          })}
          className="kiosk-btn-secondary flex items-center gap-3"
          data-testid="back-button"
        >
          <ArrowLeft className="w-6 h-6" />
          <span>Back</span>
        </button>

        <button
          onClick={handleContinue}
          disabled={!frontImage || !backImage}
          className="kiosk-btn-primary flex items-center gap-3"
          data-testid="continue-button"
        >
          <span>Process Badge</span>
          <ArrowRight className="w-6 h-6" />
        </button>
      </div>
    </div>
  );
}
